import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";
import { FilterButton, PickerOption, PickerSheet } from "./PickerSheet";

const DEFAULT_MONTHS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: new Date(0, i).toLocaleString("default", { month: "long" }),
  shortLabel: new Date(0, i).toLocaleString("default", { month: "short" }),
}));

const normalizeValue = (value, options) => {
  if (value === "" || value === null || value === undefined) return value;
  const match = options.find((opt) => String(opt.value) === String(value));
  return match ? match.value : value;
};

const getMonthDisplay = (option, useShortMonthLabel) =>
  useShortMonthLabel
    ? option.shortLabel || option.label || option.name
    : option.label || option.name;

const MobileMonthYearFilters = ({
  selectedMonth,
  selectedYear,
  onMonthChange,
  onYearChange,
  monthOptions = DEFAULT_MONTHS,
  yearOptions = [],
  allowAllMonths = false,
  showMonth = true,
  showYear = true,
  useShortMonthLabel = false,
  sx,
}) => {
  const [monthOpen, setMonthOpen] = useState(false);
  const [yearOpen, setYearOpen] = useState(false);

  const monthChoices = useMemo(() => {
    const items = monthOptions.map((opt) => ({
      ...opt,
      shortLabel:
        opt.shortLabel ||
        (typeof opt.label === "string" && opt.label.length <= 4
          ? opt.label
          : new Date(2000, Number(opt.value) - 1, 1).toLocaleString("default", {
              month: "short",
            })),
    }));
    if (allowAllMonths) {
      items.unshift({ value: "", label: "All Months", shortLabel: "All" });
    }
    return items;
  }, [allowAllMonths, monthOptions]);

  const monthLabel = useMemo(() => {
    if (!showMonth) return "";
    if (allowAllMonths && (selectedMonth === "" || selectedMonth == null)) {
      return "All Months";
    }
    const match = monthChoices.find(
      (opt) => String(opt.value) === String(selectedMonth)
    );
    if (!match) return "Month";
    return getMonthDisplay(match, useShortMonthLabel);
  }, [allowAllMonths, monthChoices, selectedMonth, showMonth, useShortMonthLabel]);

  const yearLabel = useMemo(() => {
    if (!showYear) return "";
    return selectedYear ? String(selectedYear) : "Year";
  }, [selectedYear, showYear]);

  const periodPreview = useMemo(() => {
    const monthPart =
      allowAllMonths && (selectedMonth === "" || selectedMonth == null)
        ? "All months"
        : monthLabel;
    if (showMonth && showYear) return `${monthPart} ${selectedYear || ""}`.trim();
    if (showYear) return String(selectedYear || "");
    return monthPart;
  }, [allowAllMonths, monthLabel, selectedMonth, selectedYear, showMonth, showYear]);

  const handleMonthSelect = (value) => {
    onMonthChange(value);
    setMonthOpen(false);
  };

  const handleYearSelect = (value) => {
    onYearChange(value);
    setYearOpen(false);
  };

  return (
    <>
      <Box sx={{ display: "flex", gap: 1, width: "100%", ...sx }}>
        {showMonth && (
          <FilterButton
            label={monthLabel}
            onClick={() => setMonthOpen(true)}
            isActive={monthOpen}
            flex={1}
          />
        )}
        {showYear && (
          <FilterButton
            label={yearLabel}
            onClick={() => setYearOpen(true)}
            isActive={yearOpen}
            flex={1}
          />
        )}
      </Box>

      {showMonth && (
        <PickerSheet
          open={monthOpen}
          onClose={() => setMonthOpen(false)}
          title="Select month"
          subtitle="Tap a month to filter results"
          preview={periodPreview}
          previewLabel="Selected period"
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 0.75,
            }}
          >
            {monthChoices.map((option) => {
              const isSelected =
                String(normalizeValue(selectedMonth, monthChoices)) ===
                String(option.value);
              return (
                <PickerOption
                  key={`month-${String(option.value)}`}
                  label={getMonthDisplay(option, useShortMonthLabel)}
                  isSelected={isSelected}
                  onClick={() => handleMonthSelect(option.value)}
                />
              );
            })}
          </Box>
        </PickerSheet>
      )}

      {showYear && (
        <PickerSheet
          open={yearOpen}
          onClose={() => setYearOpen(false)}
          title="Select year"
          subtitle="Tap a year to filter results"
          preview={periodPreview}
          previewLabel="Selected period"
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 0.75,
            }}
          >
            {yearOptions.map((year) => {
              const isSelected = String(selectedYear) === String(year);
              return (
                <PickerOption
                  key={year}
                  label={year}
                  isSelected={isSelected}
                  onClick={() => handleYearSelect(year)}
                />
              );
            })}
          </Box>
        </PickerSheet>
      )}
    </>
  );
};

export default MobileMonthYearFilters;
